import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Herosection from './components/Herosection';
import LiveProjectsSection from './components/LiveProjectsSection';
import TechnicalTimeline from './components/TechnicalTimeline';
import Featured from './components/Featured';
import StatsSection from './components/StatsSection';
import CTASection from './components/CTASection';
import ProjectModal from './components/ProjectModal';

const PortfolioInnovationShowcase = () => {
const projects = [
  {
    id: 1,
    title: "Interns Management System (IMS)",
    category: "Web Applications",
    year: "2025",
    status: "Live",
    image: "/images/projects/ims.png",
    description: "A comprehensive platform to streamline the recruitment, management, and evaluation of interns for companies of all sizes.",
    fullDescription: `The Interns Management System (IMS) is a robust enterprise solution designed to tackle the complexities of running a modern internship program. It centralizes everything from applicant tracking and interview scheduling to project assignment and performance reviews.

The platform significantly reduces administrative overhead, allowing companies to focus on providing a meaningful and educational experience for their interns. It also offers a transparent and engaging experience for interns, giving them a clear view of their progress and opportunities.`,
    challenge: "Managing a large-scale internship program involves a fragmented workflow with multiple tools for applicant tracking, communication, and performance evaluation. This leads to inefficiencies, data silos, and a poor experience for both administrators and interns.",
    solution: "IMS provides a single, unified dashboard for the entire internship lifecycle. It integrates applicant tracking with a project management system and a real-time feedback mechanism, ensuring a seamless and structured program.",
    architecture: `Built on a modern microservices architecture using Node.js for the backend and React with TypeScript for the frontend. The system uses a PostgreSQL database for structured relational data and Redis for session management and caching.`,
    codeSnippet: `// API endpoint for fetching intern performance data
const getInternPerformance = async (internId) => {
  const intern = await Intern.findById(internId).populate('assignedProjects');
  if (!intern) {
    throw new Error('Intern not found');
  }

  const feedbackData = await Feedback.find({ intern: internId }).sort('-date');
  
  const performanceScore = calculatePerformance(intern, feedbackData);
  return {
    intern: intern.toObject(),
    performanceScore,
    recentFeedback: feedbackData
  };
};`,
    technologies: ["React", "TypeScript", "Node.js", "Express", "PostgreSQL", "Redis", "Docker"],
    technicalHighlights: [
      "Role-based access control with granular permissions",
      "Automated workflow for application and review processes",
      "Real-time feedback and performance tracking",
      "Integrated project management tools",
      "Scalable microservices architecture"
    ],
    impactDescription: "IMS has been adopted by several companies, reducing the time spent on administrative tasks by 50% and improving intern satisfaction rates by 30%. It has successfully managed over 500 interns to date.",
    metrics: {
      internsManaged: "500+",
      companiesUsing: "5+",
      adminTimeReduced: "50%",
      internSatisfaction: "90%"
    },
    gallery: [
      {
        url: "/images/projects/ims.png",
        caption: "Main Dashboard"
      },
      {
        url: "/images/projects/ims1.png",
        caption: "Intern Profile"
      },
      {
        url: "/images/projects/ims2.png",
        caption: "Project Management Interface"
      }
    ],
    liveUrl: "https://ims.company.com",
    githubUrl: null
  },
  {
    id: 2,
    title: "DigiManual",
    category: "Education",
    year: "Ongoing",
    status: "Live",
    image: "/images/projects/dm.png",
    description: "An exam-prep platform for Cameroonian students — project manuals and expert mentors for GCE OL, AL, Probatoire, and BAC, with AI tutoring in every plan.",
    fullDescription: `DigiManual helps Cameroonian secondary school students prepare for GCE (Ordinary & Advanced Level), Probatoire, and BAC exams with subject-specific project manuals (ICT, Computer Science, Mathematics, Physics, Biology, Networking, and more), expert mentors, and AI-powered tutoring. Rated 4.9/5 by 2,300+ students.`,
    challenge: "Generic, internationally-sourced study material doesn't match what Cameroonian students are actually examined on, leaving a real gap for GCE/Probatoire/BAC-specific prep.",
    solution: "Curriculum-specific project manuals mapped directly to GCE/Probatoire/BAC subjects, paired with mentor access and AI tutoring bundled into every plan.",
    architecture: `Live web platform at digimanual.online, built by CodersHub Innovations for the Cameroonian education market.`,
    technologies: ["Web Development", "AI Tutoring Integration"],
    technicalHighlights: [
      "Manuals organized by exam level and subject",
      "AI tutoring included in every plan",
      "Mentor access alongside self-serve content"
    ],
    impactDescription: "Live and in active use, with a 4.9/5 rating from 2,300+ students.",
    metrics: {
      rating: "4.9/5",
      students: "2,300+"
    },
    gallery: [
      {
        url: "/images/projects/dm.png",
        caption: "DigiManual Homepage"
      },
      {
        url: "/images/projects/dm1.png",
        caption: "Manual Browsing"
      },
      {
        url: "/images/projects/dm2.png",
        caption: "Product Detail"
      }
    ],
    liveUrl: "https://digimanual.online",
    githubUrl: null
  },
  {
    id: 3,
    title: "TrustHaven",
    category: "Real Estate",
    year: "Ongoing",
    status: "Live",
    image: "/images/projects/th.png",
    description: "A real-estate platform live in Cameroon — the agent fee is shown before you call, and displacement fees are banned for every Verified Agent.",
    fullDescription: `TrustHaven fixes two things that make house-hunting painful in Cameroon: hidden agent fees and displacement fees charged just for showing up to view a property. Every listing and agent is verified, agent fees are shown upfront, and displacement fees are banned outright for Verified Agents.`,
    challenge: "Renters and buyers in Cameroon routinely faced undisclosed agent fees and displacement fees just to view a listing, with no way to verify legitimacy upfront.",
    solution: "Upfront fee transparency, mandatory listing/agent verification, and a structural ban on displacement fees for Verified Agents.",
    architecture: `Live web and mobile platform at gettrusthaven.com, built by CodersHub Innovations for the Cameroonian real-estate market.`,
    technologies: ["Web Development", "Mobile App Development"],
    technicalHighlights: [
      "Agent fee shown before contact is made",
      "Mandatory listing and agent verification",
      "Displacement fees banned for Verified Agents"
    ],
    impactDescription: "Live in Cameroon, onboarding verified listings and agents.",
    metrics: {},
    gallery: [
      {
        url: "/images/projects/th.png",
        caption: "TrustHaven Homepage"
      }
    ],
    liveUrl: "https://gettrusthaven.com",
    githubUrl: null
  },
  {
    id: 5,
    title: "CodersHub Community Platform",
    category: "Community Platforms",
    year: "2024",
    status: "Live",
    image: "/images/projects/ch.png",
    description: "A comprehensive learning platform connecting developers worldwide with mentorship opportunities and collaborative projects.",
    fullDescription: `CodersHub represents the evolution of developer community building, combining social networking with structured learning pathways. The platform serves as a bridge between experienced developers and those beginning their journey, fostering an environment of continuous learning and mutual support.

The platform addresses the critical gap in accessible mentorship within the tech industry, providing tools for knowledge sharing, project collaboration, and career development. Through innovative matching algorithms and comprehensive progress tracking, CodersHub has transformed how developers connect and grow together.`,
    challenge: "The tech industry faces a significant mentorship gap, with many aspiring developers struggling to find guidance and experienced professionals lacking efficient ways to share knowledge. Traditional learning platforms often lack the personal connection and real-world application that accelerates growth.",
    solution: "CodersHub creates a structured yet flexible environment where mentorship happens naturally through project collaboration, peer reviews, and guided learning paths. The platform combines social features with educational tools, making learning both effective and engaging.",
    architecture: `The platform is built on a microservices architecture using Node.js and React, with real-time communication powered by Socket.io. The backend utilizes MongoDB for user data and PostgreSQL for structured learning content, while Redis handles caching and session management.

Key architectural decisions include implementing a message queue system for handling high-volume notifications, using CDN for global content delivery, and implementing progressive web app features for mobile accessibility.`,
    codeSnippet: `// Real-time mentorship matching algorithm
const findMentorMatches = async (studentProfile) => {
  const { skills, goals, experience } = studentProfile;
  
  const mentors = await Mentor.find({
    expertise: { $in: skills },
    availability: true,
    rating: { $gte: 4.5 }
  }).populate('reviews');
  
  return mentors.map(mentor => ({
    ...mentor.toObject(),
    compatibility: calculateCompatibility(studentProfile, mentor),
    nextAvailable: getNextAvailableSlot(mentor.schedule)
  })).sort((a, b) => b.compatibility - a.compatibility);
};`,
    technologies: ["React", "Node.js", "MongoDB", "Socket.io", "Redis", "Docker", "AWS"],
    technicalHighlights: [
      "Real-time messaging system handling 10,000+ concurrent users",
      "AI-powered mentor-student matching algorithm with 95% satisfaction rate",
      "Progressive Web App with offline functionality",
      "Microservices architecture for scalability",
      "Advanced analytics dashboard for tracking learning progress"
    ],
    impactDescription: "CodersHub has revolutionized developer mentorship by creating meaningful connections between 5,000+ developers across 50+ countries. The platform has facilitated over 10,000 mentorship sessions and helped 500+ developers land their first tech jobs.",
    metrics: {
      activeUsers: "1000+",
      mentorshipSessions: "20+",
      jobPlacements: "5+",
      countries: "5+"
    },
    gallery: [
      {
        url: "/images/projects/ch.png",
        caption: "Community Dashboard Overview"
      },
      {
        url: "/images/projects/ch1.png",
        caption: "Mentorship Matching Interface"
      },
      {
        url: "/images/projects/ch.png",
        caption: "Real-time Collaboration Tools"
      },
      {
        url: "/images/projects/ch1.png",
        caption: "Analytics and Progress Tracking"
      }
    ],
    liveUrl: "https://codershub.community",
    githubUrl: "https://github.com/adrian/codershub"
  },
  {
    id: 6,
    title: "Diotal Enterprise Dashboard",
    category: "Web Applications",
    year: "2025",
    status: "Live",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
    description: "Advanced analytics dashboard for enterprise clients with real-time data visualization and predictive insights.",
    fullDescription: `The Diotal Enterprise Dashboard represents a breakthrough in business intelligence visualization, transforming complex data streams into actionable insights. Built for enterprise clients managing multi-million dollar operations, this platform processes terabytes of data daily while maintaining sub-second response times.

The dashboard serves as the central nervous system for business operations, integrating data from multiple sources including CRM systems, financial platforms, and operational databases. Through advanced machine learning algorithms, it provides predictive analytics that help businesses make data-driven decisions with confidence.`,
    challenge: "Enterprise clients were struggling with data silos and delayed reporting, making it difficult to respond quickly to market changes. Existing solutions were either too complex for daily use or lacked the depth needed for strategic decision-making.",
    solution: "A unified dashboard that aggregates data from multiple sources, applies machine learning for predictive insights, and presents information through intuitive visualizations. The solution balances complexity with usability, making advanced analytics accessible to all stakeholders.",
    architecture: `Built with a modern tech stack featuring React with TypeScript for type safety, D3.js for custom visualizations, and a Node.js backend with GraphQL for efficient data fetching. The system uses Apache Kafka for real-time data streaming and ClickHouse for high-performance analytics queries.

The architecture implements event-driven design patterns with CQRS for separating read and write operations, ensuring optimal performance for both data ingestion and query operations.`,
    codeSnippet: `// Real-time data streaming with WebSocket
const DataStreamManager = {
  connect: (dashboardId) => {
    const ws = new WebSocket(\`wss://api.diotal.com/stream/\${dashboardId}\`);
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      updateDashboardMetrics(data);
      triggerPredictiveAnalysis(data);
    };
    
    return ws;
  },
  
  updateDashboardMetrics: (data) => {
    store.dispatch(updateMetrics(data));
    notifySubscribers(data);
  }
};`,
    technologies: ["React", "TypeScript", "D3.js", "Node.js", "GraphQL", "Apache Kafka", "ClickHouse"],
    technicalHighlights: [
      "Real-time data processing handling 1M+ events per minute",
      "Custom D3.js visualizations with interactive drill-down capabilities",
      "Machine learning integration for predictive analytics",
      "Multi-tenant architecture supporting 100+ enterprise clients",
      "Advanced caching strategy reducing query times by 80%"
    ],
    impactDescription: "The dashboard has transformed decision-making processes for enterprise clients, reducing report generation time from hours to seconds and improving forecast accuracy by 40%. It currently serves 100+ enterprise clients processing over $2B in transactions monthly.",
    metrics: {
      enterpriseClients: "100+",
      dataProcessed: "1GB/day",
      responseTime: "<500ms",
      forecastAccuracy: "95%"
    },
    gallery: [
      {
        url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
        caption: "Executive Dashboard Overview"
      },
      {
        url: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop",
        caption: "Advanced Analytics Interface"
      },
      {
        url: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=600&h=400&fit=crop",
        caption: "Real-time Data Visualization"
      },
      {
        url: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=400&fit=crop",
        caption: "Predictive Analytics Dashboard"
      }
    ],
    liveUrl: "https://dashboard.diotal.com",
    githubUrl: null
  },
];
  // Mock data for live projects
  const liveProjects = [
    {
      id: 1,
      title: "Interns Management System (IMS)",
      description: "A comprehensive platform for companies to streamline intern recruitment, project tracking, and performance evaluation.",
      image: "/images/projects/ims2.png",
      technologies: ["React", "Node.js", "Express", "PostgreSQL"],
      liveMetrics: {
        internsManaged: "150",
        applicationsProcessed: "5,000+",
        activeCompanies: "25",
        placementRate: "90%"
      },
      recentFeedback: [
        {
          user: "Hiring Manager",
          rating: 5,
          comment: "IMS has completely streamlined our intern program. The ability to track projects and performance in one place is a game-changer.",
          date: "1 hour ago"
        },
        {
          user: "Chisom A.",
          rating: 5,
          comment: "Finding and applying for internships was so easy with IMS. It’s an incredibly intuitive platform.",
          date: "3 hours ago"
        }
      ]
    },
    {
      id: 2,
      title: "CodersHub Community Platform",
      description: "Real-time mentorship and collaboration platform serving developers worldwide.",
      image: "/images/projects/ch1.png",
      technologies: ["React", "Node.js", "MongoDB", "Socket.io"],
      liveMetrics: {
        activeUsers: "100",
        dailySessions: "8,234",
        mentorshipHours: "156",
        newSignups: "47"
      },
      recentFeedback: [
        {
          user: "Sarah M.",
          rating: 5,
          comment: "Amazing platform for connecting with mentors. Found my dream job through connections made here!",
          date: "2 hours ago"
        },
        {
          user: "Dev Kumar",
          rating: 5,
          comment: "The community is incredibly supportive. Love the real-time collaboration features.",
          date: "5 hours ago"
        }
      ]
    }
];

  // Mock data for technical timeline
  const timelineData = [
    {
      year: "2025",
      title: "AI & Machine Learning Integration",
      icon: "Brain",
      description: "Advanced into AI-powered applications with machine learning algorithms for personalized user experiences and predictive analytics.",
      technologies: ["TensorFlow", "Python", "PyTorch", "OpenAI API", "Scikit-learn"],
      achievements: [
        "Implemented AI tutoring system with 40% learning improvement",
        "Developed predictive analytics reducing forecast errors by 35%",
        "Created intelligent automation saving users 30% energy consumption",
        "Built natural language processing for content analysis"
      ],
      complexityLevel: 5
    },
    {
      year: "2024",
      title: "Enterprise-Scale Architecture",
      icon: "Building",
      description: "Mastered enterprise-level system design with microservices, handling millions of transactions and serving large-scale clients.",
      technologies: ["Microservices", "Kubernetes", "Docker", "Java Spring Boot", "GraphQL", "RabbitMQ"],
      achievements: [
        "Architected secure API gateway processing 10M+ daily transactions",
        "Led a team in migrating monolith to microservices architecture",
        "Implemented advanced fraud detection with a 60% reduction in false positives",
        "Deployed CI/CD pipelines for 99.99% uptime"
      ],
      complexityLevel: 4
    },
    {
      year: "2023",
      title: "Full-Stack Development & Community Building",
      icon: "Users",
      description: "Focused on end-to-end web and mobile solutions, building interactive platforms and fostering online developer communities.",
      technologies: ["React", "Node.js", "Express.js", "MongoDB", "PostgreSQL", "Socket.io"],
      achievements: [
        "Launched CodersHub platform connecting 5,000+ developers",
        "Developed real-time collaboration tools and chat features",
        "Mentored 100+ junior developers in full-stack practices",
        "Designed and implemented responsive and accessible user interfaces"
      ],
      complexityLevel: 3
    },
    {
      year: "2021",
      title: "Foundational Skills & UI/UX",
      icon: "Code",
      description: "Established a strong foundation in modern web development, focusing on clean code, component-based architecture, and user-centric design.",
      technologies: ["HTML5", "CSS3", "JavaScript (ES6+)", "React", "Redux", "Sass"],
      achievements: [
        "Developed reusable UI components for multiple projects",
        "Improved website performance by 50% through code optimization",
        "Contributed to open-source projects on GitHub",
        "Gained expertise in state management with Redux"
      ],
      complexityLevel: 2
    }
  ];

  // State for filtering projects
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTechnology, setSelectedTechnology] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const categories = ['All', ...new Set(projects.map(p => p.category))];
  const technologies = ['All', ...new Set(projects.flatMap(p => p.technologies))];

  const handleCategoryChange = (category) => setSelectedCategory(category);
  const handleTechnologyChange = (technology) => setSelectedTechnology(technology);
  const handleClearFilters = () => {
    setSelectedCategory('All');
    setSelectedTechnology('All');
  };

  const filteredProjects = projects.filter(project => {
    const matchesCategory = selectedCategory === 'All' || project.category === selectedCategory;
    const matchesTechnology = selectedTechnology === 'All' || project.technologies.includes(selectedTechnology);
    return matchesCategory && matchesTechnology;
  });

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  // Stats data from projects
  const statsData = [
    { label: "Active Users", value: "1000+", icon: "Users" },
    { label: "Transactions Processed", value: "10M+", icon: "DollarSign" },
    { label: "Energy Saved", value: "30%", icon: "Zap" },
    { label: "Developers Mentored", value: "100+", icon: "GraduationCap" }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>
        <Herosection onExplorePress={() => window.location.href = '#projects'} />
        <section id="projects" className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <LiveProjectsSection liveProjects={liveProjects} />
          </div>
        </section>
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <TechnicalTimeline timelineData={timelineData} />
          </div>
        </section>
        <Featured
          filteredProjects={filteredProjects}
          categories={categories}
          technologies={technologies}
          selectedCategory={selectedCategory}
          selectedTechnology={selectedTechnology}
          onCategoryChange={handleCategoryChange}
          onTechnologyChange={handleTechnologyChange}
          onClearFilters={handleClearFilters}
          onViewDetails={handleViewDetails}
        />
        <StatsSection statsData={statsData} />
        <CTASection />
      </main>
      <Footer />
      {selectedProject && (
        <ProjectModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          project={selectedProject}
        />
      )}
    </div>
  );
};

export default PortfolioInnovationShowcase;